import { CompanyMetaSetup } from '../components/CompanyMetaSetup.jsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import CompanyMembers from '../components/CompanyMembers.jsx';
import CompanyWorkspaceDefaults from '../components/CompanyWorkspaceDefaults.jsx';
import { billingDate, useCompanyBilling } from '../components/CompanyBilling.jsx';
import Select from '../components/Select.jsx';
import './CompanyAdmin.css';
import CompanyZapier, { CompanyZapierCard } from '../components/CompanyZapier.jsx';
import { clearZapierLeadsContext } from '../zapier-leads-context.js';
import { COMPANY_SUBPAGES, companySubpage, isCompanyZapierRoute } from '../company-navigation.js';
import { requestNavigation, setNavigationGuard } from '../shell/router.js';
import { PALETTE } from '../palette.js';
import { Button, Card, Pill } from '../components/primitives.jsx';
import {
  ConfirmationDialog,
  useConfirmationDialog,
} from '../components/ConfirmationDialog.jsx';
import ProtectedPageLoader from '../components/ProtectedPageLoader.jsx';
import { IconCheck, IconInfo, IconLink, IconMail, IconUsers } from '../components/icons.jsx';
import { ACTIVE_COMPANY_KEY, api } from '../api.js';
import { safeFailureMessage } from '../safe-failure.js';
import {
  COMPANY_CAMPAIGN_IDENTITY_RECOVERY_MESSAGE,
  ensureAndLoadCompanyBrands,
  isActiveCompanyBrand,
} from '../company-campaign-identity.js';
import {
  clearOnboardingMetaContinuation,
  onboardingMetaContinuationActive,
} from '../company-meta-setup.js';
import {
  createMetaAuthorizationLauncher,
  curatedMetaConnectionAction,
  curatedMetaConnectionPresentation,
  META_COMPANY_STORAGE_KEY,
  checkCuratedMetaAuthorization,
  curatedMetaSyncRecoveryNotice,
  disconnectCuratedMetaConnection,
  metaCredentialId,
  refreshCuratedMetaConnection,
  startCuratedMetaOAuth,
  useCuratedMetaConnection,
} from '../components/CuratedMetaConnection.jsx';
import {
  isSafeMetaUuid,
  metaPageRowsForBindings,
  normalizeInstantFormSetup,
} from '../instant-form-setup.js';

const ADMIN_ROLES = new Set(['owner', 'admin']);

export function normalizeCompanyWebsiteInput(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return null;
  const candidate = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  let url;
  try {
    url = new URL(candidate);
  } catch {
    throw new Error('Enter a valid public website using http or https.');
  }
  if (!['http:', 'https:'].includes(url.protocol) || !url.hostname || url.username || url.password) {
    throw new Error('Enter a valid public website using http or https.');
  }
  url.hash = '';
  return url.toString();
}

export function selectCompanyProfileBrand(value, companyId) {
  return (Array.isArray(value) ? value : [])
    .filter((brand) => isActiveCompanyBrand(brand, companyId))
    .sort((left, right) => (
      String(left.name || left.slug || left.id).localeCompare(String(right.name || right.slug || right.id))
    ))[0] || null;
}

function tenantHeaders(companyId) {
  return companyId ? { headers: { 'x-company-id': companyId } } : undefined;
}

function text(value) {
  return typeof value === 'string' ? value.trim() : '';
}

export async function assignCompanyMetaPage({
  requestApi = api,
  companyId,
  pageResourceId,
} = {}) {
  const normalizedCompanyId = text(companyId);
  const normalizedPageResourceId = text(pageResourceId);
  if (!normalizedCompanyId || !isSafeMetaUuid(normalizedPageResourceId)) {
    throw new Error('A company and Facebook Page are required for assignment.');
  }
  return requestApi.post(
    '/api/saas/meta/page-assignments',
    { pageResourceId: normalizedPageResourceId, confirmed: true },
    tenantHeaders(normalizedCompanyId),
  );
}

export async function verifyCompanyMetaPage({
  requestApi = api,
  companyId,
  pageResourceId,
} = {}) {
  const normalizedCompanyId = text(companyId);
  const normalizedPageResourceId = text(pageResourceId);
  if (!normalizedCompanyId || !isSafeMetaUuid(normalizedPageResourceId)) {
    throw new Error('A company and Facebook Page are required for verification.');
  }
  return requestApi.post(
    '/api/saas/meta/page-assignment-verifications',
    { pageResourceId: normalizedPageResourceId },
    tenantHeaders(normalizedCompanyId),
  );
}

export async function revokeCompanyMetaPage({
  requestApi = api,
  companyId,
  assignmentId,
  retireUnusedSetup = false,
} = {}) {
  const normalizedCompanyId = text(companyId);
  const normalizedAssignmentId = text(assignmentId);
  if (!normalizedCompanyId || !isSafeMetaUuid(normalizedAssignmentId)) {
    throw new Error('A company and Page assignment are required for revocation.');
  }
  return requestApi.del(
    `/api/saas/meta/page-assignments/${encodeURIComponent(normalizedAssignmentId)}`,
    {
      ...tenantHeaders(normalizedCompanyId),
      body: retireUnusedSetup === true
        ? { confirmed: true, retireUnusedSetup: true }
        : { confirmed: true },
    },
  );
}

// OGC-529: offered only when the server reports that the block comes from an
// unused company setup (no campaigns or unsettled work). Nothing changes in Meta.
export function metaUnusedSetupRetirementDialog({ action, companySetups = 1 } = {}) {
  if (action !== 'revoke' && action !== 'remove') return null;
  // Removal may be offered with only a Form left (the setup was retired by an
  // earlier revocation); then there is no setup to talk about.
  if (action === 'remove' && companySetups === 0) {
    return Object.freeze({
      title: 'Remove the unused Meta integration?',
      description: 'This company has no campaigns, so its Meta integration is not in use. BeyondLeads will remove the saved authorization. The Instant Form, Facebook Page, ad account and any leads stay in Meta and in BeyondLeads history, and nothing is changed in Meta. You can connect Meta again at any time.',
      confirmLabel: 'Remove unused integration',
      tone: 'danger',
    });
  }
  const outcome = action === 'revoke'
    ? 'revoke this Page assignment'
    : 'remove the Meta integration';
  return Object.freeze({
    title: 'Retire the unused Meta setup?',
    description: `This company has no campaigns, so its saved Meta setup is not in use. BeyondLeads will retire that setup so you can ${outcome}. The Facebook Page, ad account, Instant Form and any leads stay in Meta and in BeyondLeads history, and nothing is changed in Meta. You can set up Meta again at any time.`,
    confirmLabel: action === 'revoke' ? 'Retire setup and revoke' : 'Retire setup and remove',
    tone: 'danger',
  });
}

export function metaPageAssignmentDialog({
  action,
  companyName,
  pageLabel,
} = {}) {
  const exactCompanyName = text(companyName);
  const exactPageLabel = text(pageLabel);
  if (!exactCompanyName || !exactPageLabel) return null;
  if (action === 'assign') {
    return Object.freeze({
      title: `Assign ${exactPageLabel} to ${exactCompanyName}?`,
      description: 'This makes the Page available only to this company in BeyondLeads.',
      confirmLabel: 'Assign Page',
      tone: 'warning',
    });
  }
  if (action === 'revoke') {
    return Object.freeze({
      title: `Revoke the ${exactPageLabel} assignment from ${exactCompanyName}?`,
      description: 'This removes only the BeyondLeads Page assignment. It does not disconnect Meta Ads, remove the Page from Facebook, change Meta permissions, or alter existing campaigns. BeyondLeads will block revocation while Forms or campaigns still depend on this Page.',
      confirmLabel: 'Revoke assignment',
      tone: 'danger',
    });
  }
  return null;
}

export function metaPageAssignmentConfirmation(input = {}) {
  const dialog = metaPageAssignmentDialog(input);
  return dialog ? `${dialog.title} ${dialog.description}` : '';
}

const META_PAGE_DEPENDENCY_LABELS = Object.freeze({
  activeForms: Object.freeze(['Instant Form', 'Instant Forms']),
  formOperations: Object.freeze(['Form setup operation', 'Form setup operations']),
  campaigns: Object.freeze(['campaign', 'campaigns']),
  formRecoveries: Object.freeze(['Form recovery', 'Form recoveries']),
  companySetups: Object.freeze(['company Meta setup', 'company Meta setups']),
});
// Older responses carry no setup count; absence means none were reported.
const OPTIONAL_META_DEPENDENCY_FIELDS = new Set(['companySetups']);
const MAX_META_PAGE_DEPENDENCY_COUNT = 10000;

function joinedList(items) {
  if (items.length < 2) return items[0] || '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items.at(-1)}`;
}

export function metaPageAssignmentDependencyPresentation(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return Object.freeze({ summary: '', items: Object.freeze([]) });
  }
  const fields = Object.keys(META_PAGE_DEPENDENCY_LABELS);
  if (fields.some((field) => (
    !(OPTIONAL_META_DEPENDENCY_FIELDS.has(field) && value[field] === undefined)
    && (!Number.isSafeInteger(value[field])
      || value[field] < 0
      || value[field] > MAX_META_PAGE_DEPENDENCY_COUNT)
  ))) return Object.freeze({ summary: '', items: Object.freeze([]) });
  const items = fields
    .filter((field) => value[field] > 0)
    .map((field) => {
      const count = value[field];
      const labels = META_PAGE_DEPENDENCY_LABELS[field];
      const countLabel = count === MAX_META_PAGE_DEPENDENCY_COUNT
        ? `${count.toLocaleString('en-US')}+`
        : count.toLocaleString('en-US');
      return `${countLabel} ${labels[count === 1 ? 0 : 1]}`;
    });
  return Object.freeze({ summary: joinedList(items), items: Object.freeze(items) });
}

const META_REMOVAL_DEPENDENCY_LABELS = Object.freeze({
  companySetups: Object.freeze(['company Meta setup', 'company Meta setups']),
  protectedDrafts: Object.freeze(['protected campaign', 'protected campaigns']),
  forms: Object.freeze(['Instant Form', 'Instant Forms']),
  publishedCampaigns: Object.freeze(['published campaign', 'published campaigns']),
  unsettledOperations: Object.freeze(['unsettled operation', 'unsettled operations']),
});

export function metaIntegrationRemovalDialog() {
  return Object.freeze({
    title: 'Remove Meta integration?',
    description: 'This removes the saved BeyondLeads authorization and its current access projection. Saved campaign destinations remain retained, but cannot be managed until exact access is reauthorized. This does not revoke permissions in Meta, unsubscribe the Facebook Page, pause campaigns, or stop Meta delivery or spend.',
    confirmLabel: 'Remove Meta integration',
    tone: 'danger',
  });
}

export function metaIntegrationRemovalDependencyPresentation(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return Object.freeze({ summary: '', items: Object.freeze([]) });
  }
  const fields = Object.keys(META_REMOVAL_DEPENDENCY_LABELS)
    .filter((field) => !OPTIONAL_META_DEPENDENCY_FIELDS.has(field) || Object.hasOwn(value, field));
  if (Object.keys(value).length !== fields.length || fields.some((field) => (
    !Object.hasOwn(value, field)
    || !Number.isSafeInteger(value[field])
    || value[field] < 0
    || value[field] > MAX_META_PAGE_DEPENDENCY_COUNT
  ))) return Object.freeze({ summary: '', items: Object.freeze([]) });
  const items = fields
    .filter((field) => value[field] > 0)
    .map((field) => {
      const count = value[field];
      const labels = META_REMOVAL_DEPENDENCY_LABELS[field];
      const countLabel = count === MAX_META_PAGE_DEPENDENCY_COUNT
        ? `${count.toLocaleString('en-US')}+`
        : count.toLocaleString('en-US');
      return `${countLabel} ${labels[count === 1 ? 0 : 1]}`;
    });
  return Object.freeze({ summary: joinedList(items), items: Object.freeze(items) });
}

export function metaIntegrationRemovalGuardMatches(currentGuard, expected) {
  return Boolean(
    text(expected?.companyId)
    && text(expected?.credentialId)
    && text(currentGuard?.selectedCompanyId) === text(expected.companyId)
    && !currentGuard?.metaBusy
    && currentGuard?.metaConnection?.capabilities?.canRemove === true
    && metaCredentialId(currentGuard?.metaConnection?.connection) === text(expected.credentialId)
  );
}

export function metaPageRevocationGuardMatches(currentGuard, expected) {
  const current = currentGuard?.metaResources;
  if (
    !expected
    || text(currentGuard?.selectedCompanyId) !== text(expected.companyId)
    || currentGuard?.metaBusy
    || text(current?.companyId) !== text(expected.companyId)
    || current?.status !== 'ready'
    || current?.instantFormSetup?.canManage !== true
  ) return false;
  return (Array.isArray(current.instantFormSetup.assignments)
    ? current.instantFormSetup.assignments
    : []).some((assignment) => (
    text(assignment?.assignmentId) === text(expected.assignmentId)
    && text(assignment?.pageResourceId) === text(expected.pageResourceId)
    && text(assignment?.credentialId) === text(expected.credentialId)
  ));
}

export function resolveMetaAssignmentCompanyName({
  selectedCompanyId,
  activeCompany,
  memberships,
} = {}) {
  const companyId = text(selectedCompanyId);
  if (!companyId) return '';
  if (text(activeCompany?.id) === companyId) return text(activeCompany?.name);
  const membership = (Array.isArray(memberships) ? memberships : []).find((candidate) => (
    text(candidate?.companyId) === companyId
    && text(candidate?.company?.id) === companyId
  ));
  return text(membership?.company?.name);
}

function titleCase(value) {
  return String(value || '')
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'Unknown';
}

function memberName(member) {
  return member.displayName || member.email || 'Pending member';
}

function initials(name) {
  return String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase() || '?';
}

function persistActiveCompanyId(companyId) {
  try {
    if (companyId) {
      if (localStorage.getItem(ACTIVE_COMPANY_KEY) !== companyId) clearZapierLeadsContext();
      localStorage.setItem(ACTIVE_COMPANY_KEY, companyId);
    }
  } catch {}
}

export function notifyTenantContextChangeIfNeeded({
  currentCompanyId,
  nextContext,
  onTenantContextChange,
} = {}) {
  const nextCompanyId = nextContext?.activeCompany?.id || nextContext?.tenant?.companyId || null;
  if (!nextCompanyId || nextCompanyId === currentCompanyId || typeof onTenantContextChange !== 'function') {
    return false;
  }
  onTenantContextChange(nextContext);
  return true;
}

export default function CompanyAdmin({ session, route, navigate, onTenantContextChange }) {
  const companyBilling = useCompanyBilling();
  const initialMemberships = session.user?.memberships || [];
  const sessionCompanyId = session.user?.activeCompany?.id || initialMemberships[0]?.companyId || '';
  const [selectedCompanyId, setSelectedCompanyId] = useState(sessionCompanyId);
  const lastSessionCompanyId = useRef(sessionCompanyId);
  const [context, setContext] = useState(() => ({
    activeCompany: session.user?.activeCompany || null,
    role: session.user?.role || null,
    memberships: initialMemberships,
  }));
  const [policy, setPolicy] = useState(null);
  const [members, setMembers] = useState(null);
  const [brands, setBrands] = useState(null);
  const [websiteInput, setWebsiteInput] = useState('');
  const [websiteStatus, setWebsiteStatus] = useState('idle');
  const [websiteError, setWebsiteError] = useState('');
  const websiteMutationVersion = useRef(0);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviteStatus, setInviteStatus] = useState('idle');
  const [inviteResult, setInviteResult] = useState(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [brandStatus, setBrandStatus] = useState('loading');
  const [brandReload, setBrandReload] = useState(0);
  const [memberStatus, setMemberStatus] = useState('loading');
  const [memberError, setMemberError] = useState(null);
  const [defaultsEdit, setDefaultsEdit] = useState({ dirty: false, busy: false });
  const [zapierEdit, setZapierEdit] = useState({ dirty: false, busy: false });
  const [memberEdit, setMemberEdit] = useState({ dirty: false, busy: false });
  const [metaSetupOpen, setMetaSetupOpen] = useState(false);
  const [navigationNotice, setNavigationNotice] = useState('');
  const [formGeneration, setFormGeneration] = useState(0);
  const leavePending = useRef(false);
  const authorizedMetaRedirect = useRef(false);
  const confirmationDialog = useConfirmationDialog();

  const memberships = context.memberships?.length ? context.memberships : initialMemberships;
  const selectedMembership = memberships.find((membership) => membership.companyId === selectedCompanyId);
  const contextCurrent = context.activeCompany?.id === selectedCompanyId;
  const activeCompany = contextCurrent ? context.activeCompany : selectedMembership?.company || null;
  const assignmentCompanyName = resolveMetaAssignmentCompanyName({
    selectedCompanyId,
    activeCompany,
    memberships,
  });
  const role = contextCurrent ? context.role : selectedMembership?.role || null;
  const policyCurrent = policy?.companyId === selectedCompanyId;
  const actions = policyCurrent ? policy.actions || [] : [];
  const profileBrand = selectCompanyProfileBrand(brands, selectedCompanyId);
  const canEditWebsite = actions.includes('brands.write');
  const canViewMembers = actions.includes('members.read') || ADMIN_ROLES.has(role);
  const canInvite = actions.includes('members.invite');
  const isSaasSession = session.user?.auth?.type === 'supabase';
  const isCuratedMvp = isSaasSession && Boolean(session.user?.productPolicy);
  const canManageCredentials = !isCuratedMvp && actions.includes('credentials.manage');
  const onboardingContinuation = isCuratedMvp && onboardingMetaContinuationActive({
    userId: session.user?.id, companyId: selectedCompanyId,
  });
  const zapierRoute = isCompanyZapierRoute(route);
  const subpage = companySubpage(route, { onboardingContinuation });
  useEffect(() => {
    if (onboardingContinuation && !(route?.rest?.length)) navigate?.('company/connections', { replace: true });
  }, [onboardingContinuation, route?.rest?.length, navigate]);
  const metaConnection = useCuratedMetaConnection({
    companyId: selectedCompanyId,
    identityKey: JSON.stringify([session.user?.id || '', session.user?.role || '', role || '']),
    enabled: isSaasSession && isCuratedMvp && subpage === 'connections' && !zapierRoute,
  });
  const [metaBusy, setMetaBusy] = useState('');
  const [metaFeedback, setMetaFeedback] = useState(null);
  const [metaPageFeedback, setMetaPageFeedback] = useState(null);
  const [metaResources, setMetaResources] = useState({
    companyId: '',
    status: 'idle',
    connections: [],
    instantFormSetup: normalizeInstantFormSetup(),
    error: null,
  });
  const [metaResourcesNonce, setMetaResourcesNonce] = useState(0);
  // OGC-529: bumped after an unused setup is retired so the panel rereads it.
  const [metaSetupGeneration, setMetaSetupGeneration] = useState(0);
  const metaMutationVersion = useRef(0);
  const metaAuthorizationLauncher = useRef(createMetaAuthorizationLauncher());
  const metaHeadingRef = useRef(null);
  const selectedCompanyIdRef = useRef(selectedCompanyId);
  const companyActionGuardRef = useRef(null);
  selectedCompanyIdRef.current = selectedCompanyId;
  companyActionGuardRef.current = {
    assignmentCompanyName,
    metaBusy,
    metaConnection,
    metaResources,
    selectedCompanyId,
  };
  const showInitialCompanyLoading =
    isSaasSession &&
    Boolean(selectedCompanyId) &&
    !policyCurrent &&
    status !== 'error';

  useEffect(() => {
    metaMutationVersion.current += 1;
    setMetaBusy('');
    setMetaFeedback(null);
    setMetaPageFeedback(null);
  }, [selectedCompanyId]);

  useEffect(() => {
    const companyId = text(selectedCompanyId);
    if (!isSaasSession || !isCuratedMvp || !companyId || subpage !== 'connections' || zapierRoute) {
      setMetaResources({
        companyId,
        status: 'idle',
        connections: [],
        instantFormSetup: normalizeInstantFormSetup(),
        error: null,
      });
      return undefined;
    }

    let alive = true;
    setMetaResources({
      companyId,
      status: 'loading',
      connections: [],
      instantFormSetup: normalizeInstantFormSetup(),
      error: null,
    });
    api.get('/api/saas/meta/resources', tenantHeaders(companyId))
      .then((response) => {
        if (!alive) return;
        setMetaResources({
          companyId,
          status: 'ready',
          connections: Array.isArray(response?.metaConnections)
            ? response.metaConnections
            : Array.isArray(response?.connections)
              ? response.connections
              : [],
          instantFormSetup: normalizeInstantFormSetup(response?.instantFormSetup),
          error: null,
        });
      })
      .catch((resourceError) => {
        if (!alive) return;
        setMetaResources({
          companyId,
          status: 'error',
          connections: [],
          instantFormSetup: normalizeInstantFormSetup(),
          error: resourceError,
        });
      });
    return () => { alive = false; };
  }, [isCuratedMvp, isSaasSession, metaResourcesNonce, selectedCompanyId, subpage, zapierRoute]);

  useEffect(() => {
    const previousSessionCompanyId = lastSessionCompanyId.current;
    lastSessionCompanyId.current = sessionCompanyId;
    if (!sessionCompanyId || sessionCompanyId === previousSessionCompanyId) return;

    setSelectedCompanyId((currentCompanyId) => {
      if (currentCompanyId && currentCompanyId !== previousSessionCompanyId) {
        return currentCompanyId;
      }
      return sessionCompanyId;
    });
  }, [sessionCompanyId]);

  useEffect(() => {
    if (!isSaasSession || !selectedCompanyId) return;

    let alive = true;
    setStatus('loading');
    setError(null);
    setMembers(null);
    setBrands(null);
    setWebsiteInput('');
    setWebsiteStatus('idle');
    setWebsiteError('');
    websiteMutationVersion.current += 1;
    setPolicy(null);

    Promise.all([
      api.get('/api/saas/context', tenantHeaders(selectedCompanyId)),
      api.get('/api/saas/policy', tenantHeaders(selectedCompanyId)),
    ])
      .then(async ([nextContext, nextPolicy]) => {
        if (!alive) return;
        setContext(nextContext);
        setPolicy({ ...nextPolicy, companyId: selectedCompanyId });
        setStatus('ready');
        notifyTenantContextChangeIfNeeded({
          currentCompanyId: sessionCompanyId,
          nextContext,
          onTenantContextChange,
        });


      })
      .catch((err) => {
        if (!alive) return;
        setError(err);
        setStatus('error');
      });

    return () => { alive = false; };
  }, [isSaasSession, selectedCompanyId]);

  // Each subpage owns its reads. A failed roster or provider read cannot
  // block Overview, and visiting Overview never starts Meta synchronization.
  useEffect(() => {
    if (!isSaasSession || !selectedCompanyId || !policyCurrent || subpage !== 'overview') return;
    let alive = true;
    setBrandStatus('loading'); setBrands(null);
    (async () => {
      try {
        const response = actions.includes('campaign.draft.create')
          ? await ensureAndLoadCompanyBrands({ companyId: selectedCompanyId })
          : await api.get('/api/saas/brands', tenantHeaders(selectedCompanyId));
        if (!alive) return;
        const next = Array.isArray(response?.brands) ? response.brands : [];
        setBrands(next); setWebsiteInput(selectCompanyProfileBrand(next, selectedCompanyId)?.websiteUrl || '');
        setBrandStatus('ready');
      } catch { if (alive) setBrandStatus('error'); }
    })();
    return () => { alive = false; };
  }, [isSaasSession, selectedCompanyId, policy, policyCurrent, subpage, brandReload]);

  useEffect(() => {
    if (!isSaasSession || !selectedCompanyId || !policyCurrent || subpage !== 'members') return;
    let alive = true;
    setMembers(null); setMemberError(null); setMemberStatus('loading');
    if (!canViewMembers) { setMembers([]); setMemberStatus('ready'); return; }
    api.get('/api/saas/members?includeInactive=1', tenantHeaders(selectedCompanyId))
      .then(response => { if (alive) { setMembers(response.members || []); setMemberStatus('ready'); } })
      .catch(failure => { if (alive) { setMemberError(failure); setMemberStatus('error'); } });
    return () => { alive = false; };
  }, [isSaasSession, selectedCompanyId, policy, policyCurrent, canViewMembers, subpage, refreshNonce]);

  useEffect(() => {
    setInviteOpen(false); setInviteEmail(''); setInviteRole('viewer'); setInviteResult(null); setInviteStatus('idle');
    setDefaultsEdit({ dirty: false, busy: false }); setMemberEdit({ dirty: false, busy: false }); setZapierEdit({ dirty: false, busy: false });
    setMetaSetupOpen(false); setNavigationNotice(''); authorizedMetaRedirect.current = false;
    return () => { websiteMutationVersion.current += 1; };
  }, [selectedCompanyId]);

  const dirty = (subpage === 'overview' && brandStatus === 'ready' && profileBrand && canEditWebsite && websiteInput !== (profileBrand?.websiteUrl || ''))
    || Boolean(inviteEmail.trim()) || defaultsEdit.dirty || memberEdit.dirty || zapierEdit.dirty;
  const operationPending = websiteStatus === 'saving' || inviteStatus === 'sending'
    || defaultsEdit.busy || memberEdit.busy || zapierEdit.busy || Boolean(metaBusy) || metaSetupOpen;
  useEffect(() => {
    if (!dirty && !operationPending) return;
    setNavigationGuard((_target, proceed) => {
      if (operationPending) {
        setNavigationNotice(metaSetupOpen ? 'Close Meta setup before leaving. Your proposed setup is saved when you close it.' : 'Wait for the current save to finish before leaving.');
        return false;
      }
      if (leavePending.current) return false;
      leavePending.current = true;
      confirmationDialog.request({ title: 'Discard unsaved changes?',
        description: 'Your unsaved Company edits will be discarded. Saved settings and pending invitations are kept.',
        confirmLabel: 'Discard changes', tone: 'danger',
      }).then(accepted => {
        leavePending.current = false;
        if (!accepted) return;
        setWebsiteInput(profileBrand?.websiteUrl || ''); setInviteEmail(''); setInviteOpen(false);
        setDefaultsEdit({ dirty: false, busy: false }); setMemberEdit({ dirty: false, busy: false }); setZapierEdit({ dirty: false, busy: false });
        setFormGeneration(value => value + 1); setNavigationNotice('');
        setNavigationGuard(null); proceed();
      });
      return false;
    });
    const beforeUnload = event => {
      if (authorizedMetaRedirect.current) return;
      event.preventDefault(); event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => { setNavigationGuard(null); window.removeEventListener('beforeunload', beforeUnload); };
  }, [dirty, operationPending, profileBrand?.websiteUrl, metaSetupOpen, confirmationDialog.request]);

  const sortedMembers = useMemo(() => {
    const weight = { owner: 0, admin: 1, marketer: 2, viewer: 3 };
    return [...(members || [])].filter((member) => ['active', 'disabled', 'invited'].includes(member.status)).sort((a, b) => {
      const roleDelta = (weight[a.role] ?? 9) - (weight[b.role] ?? 9);
      if (roleDelta) return roleDelta;
      return memberName(a).localeCompare(memberName(b));
    });
  }, [members]);

  const roleOptions = useMemo(() => (
    role === 'owner'
      ? ['owner', 'admin', 'marketer', 'viewer']
      : ['admin', 'marketer', 'viewer']
  ), [role]);

  async function submitInvite(event) {
    event.preventDefault();
    if (!canInvite || inviteStatus === 'sending') return;
    const invitationCompanyId = selectedCompanyId;
    setInviteStatus('sending');
    setInviteResult(null);
    try {
      const result = await api.post('/api/saas/members/invitations', {
        email: inviteEmail,
        role: inviteRole,
      }, tenantHeaders(selectedCompanyId));
      if (selectedCompanyIdRef.current !== invitationCompanyId) return;
      setInviteResult(result);
      setInviteEmail('');
      setInviteRole('viewer');
      setRefreshNonce((value) => value + 1);
      setInviteStatus('sent');
    } catch (err) {
      if (selectedCompanyIdRef.current !== invitationCompanyId) return;
      setInviteResult({ error: err });
      setInviteStatus('error');
    }
  }

  async function submitCompanyWebsite(event) {
    event.preventDefault();
    if (!canEditWebsite || !profileBrand?.id || websiteStatus === 'saving') return;
    let websiteUrl;
    try {
      websiteUrl = normalizeCompanyWebsiteInput(websiteInput);
    } catch (error) {
      setWebsiteError(error.message);
      setWebsiteStatus('error');
      return;
    }

    const mutationVersion = ++websiteMutationVersion.current;
    setWebsiteStatus('saving');
    setWebsiteError('');
    try {
      const response = await api.patch(
        `/api/saas/brands/${encodeURIComponent(profileBrand.id)}`,
        { websiteUrl },
        tenantHeaders(selectedCompanyId),
      );
      if (mutationVersion !== websiteMutationVersion.current) return;
      const savedBrand = response?.brand;
      if (!savedBrand || savedBrand.id !== profileBrand.id || savedBrand.companyId !== selectedCompanyId) {
        throw new Error('Company website could not be verified after save.');
      }
      setBrands((current) => (current || []).map((brand) => (
        brand.id === savedBrand.id ? savedBrand : brand
      )));
      setWebsiteInput(savedBrand.websiteUrl || '');
      setWebsiteStatus('saved');
    } catch (error) {
      if (mutationVersion !== websiteMutationVersion.current) return;
      setWebsiteStatus('error');
      setWebsiteError(
        error?.message === 'Company website could not be verified after save.'
          ? error.message
          : 'Company website could not be saved. Try again or contact an administrator.',
      );
    }
  }

  async function connectMeta({ fromSetup = false } = {}) {
    if (metaConnection.capabilities?.canManage !== true || metaBusy) {
      // The setup modal must not finish an action that never started.
      if (fromSetup) throw new Error('Another Meta action is still running. Try again in a moment.');
      return;
    }
    const mutationCompanyId = selectedCompanyId;
    const credentialId = metaCredentialId(metaConnection.connection);
    let mutationVersion = metaMutationVersion.current;
    const isCurrent = () => {
      const guard = companyActionGuardRef.current;
      return mutationVersion === metaMutationVersion.current
        && guard.selectedCompanyId === mutationCompanyId
        && guard.metaConnection.capabilities?.canManage === true
        && (!credentialId || guard.metaConnection.capabilities?.canReauthorize === true)
        && metaCredentialId(guard.metaConnection.connection) === credentialId;
    };
    try {
      await metaAuthorizationLauncher.current({
        credentialId,
        canManage: true,
        canReauthorize: metaConnection.capabilities?.canReauthorize === true,
        requestConfirmation: fromSetup ? async () => true : confirmationDialog.request,
        isCurrent,
        start: async () => {
          if (companyActionGuardRef.current.metaBusy) return null;
          mutationVersion = ++metaMutationVersion.current;
          setMetaBusy('connect');
          setMetaFeedback(null);
          const result = await startCuratedMetaOAuth({
            companyId: mutationCompanyId, returnSurface: 'company', credentialId, isCurrent,
            // Only the helper's validated URL/current-company handoff can skip
            // the unload warning. Ordinary navigation keeps the draft guard.
            navigate: authorizationUrl => {
              authorizedMetaRedirect.current = true;
              try { window.location.assign(authorizationUrl); }
              catch (failure) { authorizedMetaRedirect.current = false; throw failure; }
            },
          });
          if (!result.started && isCurrent()) setMetaBusy('');
          return result;
        },
      });
    } catch (error) {
      if (mutationVersion !== metaMutationVersion.current) return;
      setMetaBusy('');
      // The setup modal shows its own failure; the page notice is for direct actions.
      if (fromSetup) throw error;
      setMetaFeedback({ tone: 'error', message: safeFailureMessage(error, 'integration') });
    }
  }

  async function refreshMeta() {
    if (metaConnection.capabilities?.canRefresh !== true || metaBusy) return;
    const mutationVersion = ++metaMutationVersion.current;
    setMetaBusy('refresh');
    setMetaFeedback(null);
    try {
      const result = await refreshCuratedMetaConnection({
        companyId: selectedCompanyId,
        credentialId: metaCredentialId(metaConnection.connection),
      });
      if (mutationVersion !== metaMutationVersion.current) return;
      // OGC-417. A retry whose budget was already spent now returns a recovered
      // lineage instead of a refusal. Reporting that as "Meta resources
      // refreshed." would hide a bounded action the owner should know they
      // spent, so the shared notice names it and what remains.
      setMetaFeedback({
        tone: 'success',
        message: curatedMetaSyncRecoveryNotice(result) || 'Meta resources refreshed.',
      });
      metaConnection.reload();
      setMetaResourcesNonce((value) => value + 1);
    } catch (error) {
      if (mutationVersion !== metaMutationVersion.current) return;
      setMetaFeedback({ tone: 'error', message: safeFailureMessage(error, 'integration') });
      metaConnection.reload();
    } finally {
      if (mutationVersion === metaMutationVersion.current) setMetaBusy('');
    }
  }

  async function checkMetaAuthorization() {
    const credentialId = metaCredentialId(metaConnection.connection);
    if (metaConnection.capabilities?.canCheckAuthorization !== true || !credentialId || metaBusy) return;
    const mutationVersion = ++metaMutationVersion.current;
    setMetaBusy('check');
    setMetaFeedback(null);
    try {
      await checkCuratedMetaAuthorization({
        companyId: selectedCompanyId,
        credentialId,
      });
      if (mutationVersion !== metaMutationVersion.current) return;
      setMetaFeedback({ tone: 'success', message: 'Meta authorization check queued.' });
      metaConnection.reload();
    } catch (error) {
      if (mutationVersion !== metaMutationVersion.current) return;
      setMetaFeedback({ tone: 'error', message: safeFailureMessage(error, 'integration') });
      metaConnection.reload();
    } finally {
      if (mutationVersion === metaMutationVersion.current) setMetaBusy('');
    }
  }

  async function removeMetaIntegration() {
    const credentialId = metaCredentialId(metaConnection.connection);
    if (metaConnection.capabilities?.canRemove !== true || !credentialId || metaBusy) return;
    const mutationCompanyId = selectedCompanyId;
    const approved = await confirmationDialog.request({
      eyebrow: 'Meta Ads connection',
      ...metaIntegrationRemovalDialog(),
    });
    const currentGuard = companyActionGuardRef.current;
    if (
      !approved
      || !metaIntegrationRemovalGuardMatches(currentGuard, {
        companyId: mutationCompanyId,
        credentialId,
      })
    ) return;
    let retireUnusedSetup = false;
    let retiredSetups = 0;
    for (;;) {
      const mutationVersion = ++metaMutationVersion.current;
      setMetaBusy('remove');
      setMetaFeedback(null);
      try {
        await disconnectCuratedMetaConnection({ companyId: mutationCompanyId, credentialId, retireUnusedSetup });
        if (mutationVersion !== metaMutationVersion.current) return;
        setMetaFeedback({
          tone: 'success',
          message: retiredSetups > 0
            ? 'Meta integration removed. The unused Meta setup was retired.'
            : 'Meta integration removed.',
        });
        metaConnection.reload();
        setMetaResourcesNonce((value) => value + 1);
        if (retireUnusedSetup) setMetaSetupGeneration((value) => value + 1);
        return;
      } catch (error) {
        if (mutationVersion !== metaMutationVersion.current) return;
        if (
          !retireUnusedSetup
          && error?.code === 'meta_integration_removal_blocked'
          && error?.retirableUnusedSetup === true
        ) {
          setMetaBusy('');
          retiredSetups = Number.isSafeInteger(error.dependencyCounts?.companySetups)
            ? error.dependencyCounts.companySetups
            : 0;
          const retire = await confirmationDialog.request({
            eyebrow: 'Meta Ads connection',
            ...metaUnusedSetupRetirementDialog({ action: 'remove', companySetups: retiredSetups }),
          });
          if (
            retire
            && mutationVersion === metaMutationVersion.current
            && metaIntegrationRemovalGuardMatches(companyActionGuardRef.current, {
              companyId: mutationCompanyId,
              credentialId,
            })
          ) {
            retireUnusedSetup = true;
            continue;
          }
          if (mutationVersion !== metaMutationVersion.current) return;
        }
        const dependencyPresentation = error?.code === 'meta_integration_removal_blocked'
          ? metaIntegrationRemovalDependencyPresentation(error.dependencyCounts)
          : { summary: '' };
        setMetaFeedback({
          tone: 'error',
          message: dependencyPresentation.summary
            ? `${safeFailureMessage(error, 'integration')} Dependencies: ${dependencyPresentation.summary}.`
            : safeFailureMessage(error, 'integration'),
        });
        return;
      } finally {
        if (mutationVersion === metaMutationVersion.current) setMetaBusy('');
      }
    }
  }

  async function revokeMetaPage(row) {
    if (
      metaBusy
      || metaResources.instantFormSetup.canManage !== true
      || metaResources.companyId !== selectedCompanyId
      || !assignmentCompanyName
      || !row?.assignmentId
    ) return;
    const dialog = metaPageAssignmentDialog({
      action: 'revoke',
      companyName: assignmentCompanyName,
      pageLabel: row.label,
    });
    if (!dialog) return;
    const mutationCompanyId = selectedCompanyId;
    const expectedBinding = Object.freeze({
      companyId: mutationCompanyId,
      assignmentId: row.assignmentId,
      pageResourceId: row.pageResourceId,
      credentialId: row.credentialId,
    });
    const approved = await confirmationDialog.request({
      eyebrow: 'Facebook Page assignment',
      ...dialog,
    });
    const currentGuard = companyActionGuardRef.current;
    if (!approved || currentGuard?.selectedCompanyId !== mutationCompanyId) return;
    if (!metaPageRevocationGuardMatches(currentGuard, expectedBinding)) {
      setMetaPageFeedback({
        tone: 'error',
        assignmentId: row.assignmentId,
        message: 'The Page assignment changed while confirmation was open. Review the current assignment and try again.',
      });
      return;
    }
    let retireUnusedSetup = false;
    for (;;) {
      const mutationVersion = ++metaMutationVersion.current;
      setMetaBusy(`revoke:${row.assignmentId}`);
      setMetaPageFeedback(null);
      try {
        await revokeCompanyMetaPage({
          companyId: mutationCompanyId,
          assignmentId: row.assignmentId,
          retireUnusedSetup,
        });
        if (
          mutationVersion !== metaMutationVersion.current
          || selectedCompanyIdRef.current !== mutationCompanyId
        ) return;
        setMetaPageFeedback({
          tone: 'success',
          assignmentId: row.assignmentId,
          message: retireUnusedSetup
            ? `${row.label} is no longer assigned to ${assignmentCompanyName}. The unused Meta setup was retired.`
            : `${row.label} is no longer assigned to ${assignmentCompanyName}.`,
        });
        setMetaResourcesNonce((value) => value + 1);
        if (retireUnusedSetup) setMetaSetupGeneration((value) => value + 1);
        return;
      } catch (assignmentError) {
        if (mutationVersion !== metaMutationVersion.current) return;
        if (
          !retireUnusedSetup
          && assignmentError?.code === 'meta_company_page_assignment_in_use'
          && assignmentError?.retirableUnusedSetup === true
        ) {
          setMetaBusy('');
          const retire = await confirmationDialog.request({
            eyebrow: 'Facebook Page assignment',
            ...metaUnusedSetupRetirementDialog({ action: 'revoke' }),
          });
          if (
            retire
            && mutationVersion === metaMutationVersion.current
            && metaPageRevocationGuardMatches(companyActionGuardRef.current, expectedBinding)
          ) {
            retireUnusedSetup = true;
            continue;
          }
          if (mutationVersion !== metaMutationVersion.current) return;
        }
        setMetaPageFeedback({
          tone: 'error',
          assignmentId: row.assignmentId,
          code: text(assignmentError?.code),
          dependencyCounts: assignmentError?.dependencyCounts,
          message: safeFailureMessage(assignmentError, 'integration'),
        });
        if (assignmentError?.code !== 'meta_company_page_assignment_in_use') {
          setMetaResourcesNonce((value) => value + 1);
        }
        return;
      } finally {
        if (mutationVersion === metaMutationVersion.current) setMetaBusy('');
      }
    }
  }

  function metaSetupCompleted(_next, { entry, advance = true } = {}) {
    metaConnection.reload();
    setMetaResourcesNonce((value) => value + 1);
    if (entry === 'onboarding') clearOnboardingMetaContinuation();
    if (entry === 'onboarding' && advance) {
      // The completion action has already committed the setup and closed its
      // modal. Release this guard synchronously: the effect reporting that
      // close runs after this callback and must not block the verified handoff.
      setMetaSetupOpen(false);
      setNavigationGuard(null);
      navigate?.('curated-campaign', { force: true });
      return;
    }
    setMetaFeedback({ tone: 'success', message: 'Meta setup saved. You’re ready to create campaigns.' });
    setTimeout(() => metaHeadingRef.current?.focus({ preventScroll: true }), 0);
  }

  const metaManagement = companyMetaManagement({
    metaConnection,
    metaResources,
    selectedCompanyId,
    assignmentCompanyName,
    metaBusy,
    onRenew: () => connectMeta(),
    onCheckAuthorization: checkMetaAuthorization,
    onRefresh: refreshMeta,
    onRemove: removeMetaIntegration,
    onRevokePage: revokeMetaPage,
  });

  if (!isSaasSession) {
    return (
      <PageShell>
        <Header activeCompany={null} role={null} />
        <EmptyState
          title="SaaS company context unavailable"
          body="Sign in with a Supabase SaaS account to view company membership controls."
        />
      </PageShell>
    );
  }

  if (showInitialCompanyLoading) {
    return (
      <PageShell>
        <Header activeCompany={activeCompany} role={role} />
        <ProtectedPageLoader />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Header activeCompany={activeCompany} role={role} />
      <nav className="bl-company-subnav" aria-label="Company pages">
        {COMPANY_SUBPAGES.map(page => <a key={page} href={`#/company/${page}`} onClick={event => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault(); navigate?.(`company/${page}`);
        }} aria-current={subpage === page ? 'page' : undefined}>{titleCase(page)}</a>)}
      </nav>
      {navigationNotice && <p role="status">{navigationNotice}</p>}
      {status === 'error' && <EmptyState compact title="Company data unavailable" body={safeFailureMessage(error, 'members')} tone="warn" />}

      {subpage === 'overview' && <>
      <section style={gridStyle}>
        <Card style={panelStyle}>
          <SectionTitle icon={<IconUsers size={16} />} title="Company Context" />
          <div style={{ display: 'grid', gap: 14 }}>
            <FieldLabel>Active company</FieldLabel>
            {isCuratedMvp ? <div><strong>{activeCompany?.name || 'Company'}</strong><p style={{ margin: '6px 0 0', fontSize: 13 }}>Use the Company selector in navigation to switch workspace.</p></div> : (
            <Select
              className="bl-select-block"
              aria-label="Active company"
              value={selectedCompanyId}
              onChange={(nextCompanyId) => {
                const switchCompany = () => {
                metaMutationVersion.current += 1;
                setMetaBusy('');
                setMetaFeedback(null);
                try {
                  sessionStorage.removeItem(META_COMPANY_STORAGE_KEY);
                } catch {}
                persistActiveCompanyId(nextCompanyId);
                setSelectedCompanyId(nextCompanyId);
                };
                if (requestNavigation('#/company/overview', switchCompany)) switchCompany();
              }}
              disabled={memberships.length <= 1}
              style={selectStyle}
              options={memberships.map((membership) => ({
                value: membership.companyId,
                label: membership.company?.name || membership.companyId,
              }))}
            />)}
            <div style={contextGridStyle}>
              <ContextStat label="Role" value={titleCase(role)} />
              <ContextStat label="Memberships" value={String(memberships.length || 0)} />
              <ContextStat label="Tenant" value={activeCompany?.slug || 'not set'} />
            </div>
            {companyBilling.status === 'ready' && companyBilling.billing?.companyId === selectedCompanyId && companyBilling.billing.exemptionActive ? <div><Pill tone="accent">Billing exemption</Pill><p style={{ margin: '8px 0 0', fontSize: 13 }}>Payment exemption {companyBilling.billing.exemptionExpiresAt ? `ends ${billingDate(companyBilling.billing.exemptionExpiresAt, true)}` : 'remains active until removed by a platform admin'}. Existing subscriptions and charges continue unchanged.</p></div> : null}
          </div>
        </Card>

        <Card style={panelStyle}>
          <SectionTitle icon={<IconCheck size={16} />} title="Access" />
          <div style={{ display: 'grid', gap: 10 }}>
            <AccessRow
              label="View company members"
              enabled={canViewMembers}
            />
            <AccessRow
              label="Invite users"
              enabled={canInvite}
              pending={!canInvite && ADMIN_ROLES.has(role)}
            />
            {!isCuratedMvp && (
              <AccessRow
                label="Manage credentials"
                enabled={canManageCredentials}
              />
            )}
          </div>
        </Card>
      </section>

      <Card style={{ ...panelStyle, marginTop: 18 }}>
        <SectionTitle icon={<IconLink size={16} />} title="Company Website" />
        {brandStatus === 'loading' ? <ProtectedPageLoader minHeight={120} /> : brandStatus === 'error' ? (
          <div role="alert"><p>Company website could not be loaded.</p><Button size="sm" variant="ghost" onClick={() => setBrandReload(value => value + 1)}>Try again</Button></div>
        ) : !profileBrand ? (
          <EmptyState
            compact
            title="Company campaign profile is still initializing"
            body={COMPANY_CAMPAIGN_IDENTITY_RECOVERY_MESSAGE}
            tone="warn"
          />
        ) : (
          <form onSubmit={submitCompanyWebsite} className="bl-company-website-form">
            <label htmlFor="company-website-url" style={websiteLabelStyle}>Website URL</label>
            <div className="bl-company-website-controls">
              <input
                id="company-website-url"
                type="text"
                inputMode="url"
                value={websiteInput}
                onChange={(event) => {
                  setWebsiteInput(event.target.value);
                  setWebsiteStatus('idle');
                  setWebsiteError('');
                }}
                readOnly={!canEditWebsite}
                disabled={websiteStatus === 'saving'}
                placeholder="https://example.com"
                aria-describedby="company-website-help company-website-status"
                style={inputStyle}
              />
              {canEditWebsite && (
                <Button
                  type="submit"
                  variant="accent"
                  size="sm"
                  disabled={websiteStatus === 'saving'}
                  style={{ justifyContent: 'center', minHeight: 42 }}
                >
                  <IconLink size={14} /> {websiteStatus === 'saving' ? 'Saving' : 'Save website'}
                </Button>
              )}
              {!canEditWebsite && (
                <Pill tone="neutral" style={{ alignSelf: 'center', justifySelf: 'start' }}>View only</Pill>
              )}
            </div>
            <span id="company-website-help" style={websiteHelpStyle}>
              Saved to the company profile only. Campaign setup will not ask for or scrape this website.
            </span>
            <span
              id="company-website-status"
              aria-live="polite"
              style={{
                minHeight: 18,
                color: websiteStatus === 'error' ? PALETTE.danger : PALETTE.success,
                fontSize: 12,
              }}
            >
              {websiteStatus === 'saved' ? 'Company website saved.' : websiteError}
            </span>
          </form>
        )}
      </Card>

      {isSaasSession && <CompanyWorkspaceDefaults key={`${selectedCompanyId}:${formGeneration}`} companyId={selectedCompanyId} role={role} onEditStateChange={setDefaultsEdit} />}

      </>}

      {zapierRoute && isCuratedMvp && <CompanyZapier key={`${selectedCompanyId}:${session.user?.id}:${role}:${formGeneration}`} companyId={selectedCompanyId} companyName={activeCompany?.name || 'Company'} userId={session.user?.id} role={role} navigate={navigate} onEditStateChange={setZapierEdit} />}
      {subpage === 'connections' && !zapierRoute && isCuratedMvp && (
        <>
          {[metaFeedback, metaPageFeedback].map((feedback, index) => {
            const notice = companyMetaFeedbackPresentation(feedback);
            return notice && (
              <div
                key={index}
                id={feedback.assignmentId ? `meta-page-assignment-feedback-${feedback.assignmentId}` : undefined}
                className={`bl-msp bl-msp-notice${notice.tone === 'attention' ? ' is-attention' : ''}`}
                role={notice.tone === 'attention' ? 'alert' : 'status'}
              >
                {notice.tone === 'attention'
                  ? <span aria-hidden="true">!</span>
                  : <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>}
                <span>{notice.title && <strong>{notice.title}. </strong>}{notice.message}</span>
              </div>
            );
          })}
          <section className="bl-company-meta-settings" aria-labelledby="company-meta-settings-title">
            <header className="bl-company-meta-settings-heading">
              <span className="bl-kicker">Company connections</span>
              <h2 id="company-meta-settings-title" tabIndex={-1} ref={metaHeadingRef}>Meta connection settings</h2>
              <p>Choose the Meta setup this company uses for new campaigns.</p>
            </header>
            <CompanyMetaSetup
              key={`${selectedCompanyId}:${metaSetupGeneration}`}
              companyId={selectedCompanyId} companyName={assignmentCompanyName || 'Company'}
              metaConnection={metaConnection} onAuthorize={() => connectMeta({ fromSetup: true })}
              onCheckAuthorization={checkMetaAuthorization} onRefresh={refreshMeta}
              management={metaManagement}
              onboarding={{ active: onboardingContinuation, onClose: clearOnboardingMetaContinuation }}
              onModalStateChange={setMetaSetupOpen}
              onModalClose={() => setMetaResourcesNonce((value) => value + 1)}
              onComplete={metaSetupCompleted}
            />
          </section>
          <CompanyZapierCard companyId={selectedCompanyId} role={role} navigate={navigate} />
        </>
      )}

      {subpage === 'members' && <Card style={{ ...panelStyle, marginTop: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
          <SectionTitle icon={<IconUsers size={16} />} title="Members" />
          <Button
            variant="ghost"
            size="sm"
            disabled={!canInvite}
            title={canInvite ? 'Invite a user to this company' : 'Your role cannot invite users'}
            onClick={() => setInviteOpen((value) => !value)}
          >
            <IconMail size={14} /> Invite
          </Button>
        </div>

        {inviteOpen && (
          <InvitePanel
            email={inviteEmail}
            role={inviteRole}
            roles={roleOptions}
            status={inviteStatus}
            result={inviteResult}
            canInvite={canInvite}
            onEmailChange={setInviteEmail}
            onRoleChange={setInviteRole}
            onSubmit={submitInvite}
          />
        )}

        {memberStatus === 'loading' ? (
          <ProtectedPageLoader minHeight={180} style={{ padding: 18 }} />
        ) : memberStatus === 'error' ? (
          <div><EmptyState compact title="Company data unavailable" body={safeFailureMessage(memberError, 'members')} tone="warn" /><Button size="sm" variant="ghost" onClick={() => setRefreshNonce(value => value + 1)}>Try again</Button></div>
        ) : !canViewMembers ? (
          <EmptyState
            compact
            title="Member roster is admin-only"
            body="Your current role can work on campaigns, but user and credential administration is not available."
            tone="warn"
          />
        ) : sortedMembers.length === 0 ? (
          <EmptyState compact title="No members found" body="This company has no visible active or invited members." />
        ) : (
          <CompanyMembers key={`${selectedCompanyId}:${formGeneration}`} onEditStateChange={setMemberEdit} members={sortedMembers} companyId={selectedCompanyId}
            companyName={activeCompany?.name || 'this company'} actorRole={role} actorId={session.user?.id}
            onChanged={() => setRefreshNonce((value) => value + 1)} />
        )}
      </Card>}
      <ConfirmationDialog {...confirmationDialog.dialogProps} />
    </PageShell>
  );
}

// OGC-527: the assigned Pages a manager may revoke from Connection options.
// Rows join only exact tenant bindings; stale or foreign snapshots yield none.
export function companyMetaAssignedPages(snapshot, companyId) {
  const exactCompanyId = text(companyId);
  if (!exactCompanyId || text(snapshot?.companyId) !== exactCompanyId || snapshot?.status !== 'ready') return [];
  const setup = normalizeInstantFormSetup(snapshot.instantFormSetup);
  return metaPageRowsForBindings(Array.isArray(snapshot.connections) ? snapshot.connections : [], setup.assignments);
}

// Connection options for the simplified Company Meta panel. Each action keeps
// its existing handler, confirmation dialog, capability and dependency guard.
export function companyMetaManagement({
  metaConnection,
  metaResources,
  selectedCompanyId,
  assignmentCompanyName,
  metaBusy = '',
  onRenew,
  onCheckAuthorization,
  onRefresh,
  onRemove,
  onRevokePage,
} = {}) {
  const capabilities = metaConnection?.status === 'ready' ? metaConnection.capabilities || {} : {};
  const canManage = capabilities.canManage === true;
  const payload = metaConnection?.payload || {};
  const credentialId = metaCredentialId(metaConnection?.connection);
  const action = curatedMetaConnectionAction(payload, capabilities);
  const presentation = metaConnection?.status === 'ready' ? curatedMetaConnectionPresentation(payload) : null;
  const busy = Boolean(metaBusy);
  const rows = [];
  if (action?.kind === 'check_authorization') {
    rows.push({ key: 'check', title: 'Meta authorization', description: presentation?.description || '', action: action.label, busy: metaBusy === 'check', busyLabel: 'Checking…', disabled: busy, onClick: onCheckAuthorization });
  }
  if (action?.kind === 'refresh') {
    rows.push({ key: 'refresh', title: 'Meta resource sync', description: presentation?.description || '', action: action.label, busy: metaBusy === 'refresh', busyLabel: 'Retrying…', disabled: busy, onClick: onRefresh });
  }
  if (credentialId && capabilities.canRefresh === true && action?.kind !== 'refresh') {
    rows.push({ key: 'refresh-assets', title: 'Meta assets', description: 'Refresh Facebook Pages and ad accounts from Meta.', action: 'Refresh assets', busy: metaBusy === 'refresh', busyLabel: 'Refreshing…', disabled: busy, onClick: onRefresh });
  }
  if (credentialId && capabilities.canReauthorize === true) {
    rows.push({ key: 'renew', title: 'Renew Meta authorization', description: 'Sign in again if Meta access needs to be renewed.', action: 'Renew access', busy: metaBusy === 'connect', busyLabel: 'Opening Meta…', disabled: busy, onClick: onRenew });
  }
  const pageManager = metaResources?.instantFormSetup?.canManage === true && Boolean(text(assignmentCompanyName));
  for (const page of pageManager ? companyMetaAssignedPages(metaResources, selectedCompanyId) : []) {
    rows.push({
      key: `page:${page.assignmentId}`,
      title: 'Facebook Page assignment',
      description: page.label,
      action: 'Revoke assignment',
      danger: true,
      busy: metaBusy === `revoke:${page.assignmentId}`,
      busyLabel: 'Revoking…',
      disabled: busy,
      describedBy: `meta-page-assignment-feedback-${page.assignmentId}`,
      onClick: () => onRevokePage?.(page),
    });
  }
  if (credentialId && capabilities.canRemove === true) {
    rows.push({ key: 'remove', title: 'Remove Meta integration', description: 'Remove the saved BeyondLeads connection.', action: 'Remove integration', danger: true, busy: metaBusy === 'remove', busyLabel: 'Removing…', disabled: busy, onClick: onRemove });
  }
  return {
    canManage,
    rows,
    // OGC-532: only a successful read yields a label; the panel heading owns
    // the loading and unavailable wording.
    connectionLabel: presentation
      ? (presentation.state === 'valid' ? 'Connected' : presentation.label)
      : null,
  };
}

// Blocked removal and revocation keep the returned safe reason; nothing is
// cleared optimistically.
export function companyMetaFeedbackPresentation(feedback) {
  if (!feedback?.message && feedback?.code !== 'meta_company_page_assignment_in_use') return null;
  if (feedback.code === 'meta_company_page_assignment_in_use') {
    const dependencies = metaPageAssignmentDependencyPresentation(feedback.dependencyCounts);
    return Object.freeze({
      tone: 'attention',
      title: 'Page assignment is still in use',
      message: `${dependencies.summary
        ? `BeyondLeads kept this Page assigned because it is used by ${dependencies.summary}.`
        : feedback.message || ''} The Meta connection and Page assignment were not changed.`.trim(),
    });
  }
  return Object.freeze({ tone: feedback.tone === 'error' ? 'attention' : 'success', title: '', message: feedback.message });
}

function PageShell({ children }) {
  return (
    <div style={{ padding: 'clamp(18px, 4vw, 40px) clamp(16px, 4vw, 40px) 64px', fontFamily: 'var(--active-font-ui, Inter, sans-serif)' }}>
      {children}
    </div>
  );
}

function Header({ activeCompany, role }) {
  return (
    <header style={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between', gap: 18, marginBottom: 24, flexWrap: 'wrap' }}>
      <div>
        <div style={{ fontSize: 11, letterSpacing: 0, textTransform: 'uppercase', color: PALETTE.ink3, marginBottom: 6 }}>Company</div>
        <h1 style={{
          fontFamily: 'Instrument Serif, serif',
          fontStyle: 'italic',
          fontSize: 40,
          fontWeight: 400,
          margin: 0,
          color: PALETTE.ink,
          letterSpacing: 0,
        }}>
          {activeCompany?.name || 'Company Admin'}
        </h1>
      </div>
      <Pill tone={ADMIN_ROLES.has(role) ? 'accent' : 'neutral'}>{titleCase(role)}</Pill>
    </header>
  );
}

function SectionTitle({ icon, title }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: PALETTE.ink, fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
      <span style={{ color: PALETTE.accent }}>{icon}</span>
      {title}
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <label style={{ fontSize: 11, letterSpacing: 0, textTransform: 'uppercase', color: PALETTE.ink3, fontWeight: 700 }}>
      {children}
    </label>
  );
}

function ContextStat({ label, value }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, color: PALETTE.ink3, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: PALETTE.ink, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
    </div>
  );
}

function AccessRow({ label, enabled, pending = false }) {
  const tone = enabled ? 'live' : pending ? 'warn' : 'neutral';
  const text = enabled ? 'Enabled' : pending ? 'Later' : 'Restricted';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 36 }}>
      <span style={{ width: 22, height: 22, borderRadius: 999, display: 'grid', placeItems: 'center', background: enabled ? PALETTE.successSoft : PALETTE.surfaceAlt, color: enabled ? PALETTE.success : PALETTE.ink3 }}>
        {enabled ? <IconCheck size={13} /> : <IconInfo size={13} />}
      </span>
      <span style={{ flex: 1, fontSize: 13, color: PALETTE.ink2 }}>{label}</span>
      <Pill tone={tone} style={{ fontSize: 10, padding: '3px 8px' }}>{text}</Pill>
    </div>
  );
}

function InvitePanel({
  email,
  role,
  roles,
  status,
  result,
  canInvite,
  onEmailChange,
  onRoleChange,
  onSubmit,
}) {
  const sending = status === 'sending';
  const message = inviteStatusMessage(status, result);

  return (
    <form onSubmit={onSubmit} style={invitePanelStyle}>
      <div style={inviteGridStyle}>
        <label style={{ display: 'grid', gap: 6, minWidth: 0 }}>
          <FieldLabel>Email</FieldLabel>
          <input
            type="email"
            value={email}
            onChange={(event) => onEmailChange(event.target.value)}
            required
            disabled={!canInvite || sending}
            placeholder="teammate@example.com"
            style={inputStyle}
          />
        </label>
        <label style={{ display: 'grid', gap: 6, minWidth: 0 }}>
          <FieldLabel>Role</FieldLabel>
          <Select
            className="bl-select-block"
            value={role}
            onChange={onRoleChange}
            disabled={!canInvite || sending}
            style={selectStyle}
            options={roles.map((item) => ({ value: item, label: titleCase(item) }))}
          />
        </label>
        <Button
          type="submit"
          variant="accent"
          size="sm"
          disabled={!canInvite || sending}
          style={{ alignSelf: 'end', justifyContent: 'center', minHeight: 42 }}
        >
          <IconMail size={14} /> {sending ? 'Sending' : 'Send Invite'}
        </Button>
      </div>
      {message && (
        <div style={{
          marginTop: 12,
          padding: '10px 12px',
          border: `1px solid ${status === 'error' ? '#E8C9C1' : PALETTE.lineSoft}`,
          borderRadius: 8,
          background: status === 'error' ? PALETTE.dangerSoft : PALETTE.surface,
          color: status === 'error' ? PALETTE.danger : PALETTE.ink2,
          fontSize: 13,
          lineHeight: 1.45,
        }}>
          {message}
        </div>
      )}
    </form>
  );
}

function inviteStatusMessage(status, result) {
  if (status === 'sent') {
    const delivery = result?.email?.status;
    if (delivery === 'sent') return 'Invite created and email accepted by Resend.';
    if (delivery === 'skipped') return 'Invite created. Email provider is not configured for this environment.';
    if (delivery === 'failed') return 'Invite created, but email delivery failed. Check provider configuration before retrying.';
    return 'Invite created.';
  }
  if (status === 'error') return safeFailureMessage(result?.error, 'member-invite');
  return '';
}

function MemberRow({ member }) {
  const name = memberName(member);
  const isInvited = member.status === 'invited';
  return (
    <div data-company-member-row style={{
      display: 'flex',
      gap: 12,
      alignItems: 'center',
      minWidth: 0,
      padding: '12px 14px',
      border: `1px solid ${PALETTE.lineSoft}`,
      borderRadius: 10,
      background: PALETTE.surface,
    }}>
      <div style={{
        width: 36,
        height: 36,
        borderRadius: 999,
        background: isInvited ? PALETTE.accentTint : PALETTE.surfaceAlt,
        color: isInvited ? PALETTE.accentDeep : PALETTE.ink,
        display: 'grid',
        placeItems: 'center',
        fontSize: 12,
        fontWeight: 700,
      }}>
        {initials(name)}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, color: PALETTE.ink, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
        <div style={{ fontSize: 12, color: PALETTE.ink3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.email || member.userId || member.id}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end', marginLeft: 'auto' }}>
        <Pill tone={member.role === 'owner' ? 'accent' : 'neutral'}>{titleCase(member.role)}</Pill>
        <Pill tone={isInvited ? 'warn' : 'live'}>{titleCase(member.status)}</Pill>
      </div>
    </div>
  );
}

function EmptyState({ title, body, compact = false, tone = 'neutral' }) {
  const color = tone === 'warn' ? PALETTE.warn : PALETTE.ink3;
  return (
    <div style={{
      padding: compact ? 18 : 28,
      border: `1px solid ${PALETTE.lineSoft}`,
      borderRadius: 10,
      background: tone === 'warn' ? '#FFF8EA' : PALETTE.surface,
      color,
    }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: PALETTE.ink, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13, lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}

const panelStyle = {
  borderRadius: 10,
  boxShadow: '0 1px 2px rgba(26,23,20,0.03)',
};

const panelHeaderStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  padding: 18,
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
  gap: 18,
};

const contextGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 12,
  paddingTop: 4,
};

const selectStyle = {
  width: '100%',
  minHeight: 42,
  padding: '9px 12px',
  fontSize: 13,
  fontFamily: 'var(--active-font-ui, Inter, sans-serif)',
  color: PALETTE.ink,
  backgroundColor: PALETTE.surface,
  border: `1px solid ${PALETTE.controlBorder}`,
  borderRadius: 9,
  boxSizing: 'border-box',
};

const inputStyle = {
  width: '100%',
  minHeight: 42,
  padding: '9px 12px',
  fontSize: 13,
  fontFamily: 'var(--active-font-ui, Inter, sans-serif)',
  color: PALETTE.ink,
  background: PALETTE.surface,
  border: `1px solid ${PALETTE.controlBorder}`,
  borderRadius: 9,
  boxSizing: 'border-box',
};

const invitePanelStyle = {
  marginBottom: 16,
  padding: 14,
  border: `1px solid ${PALETTE.lineSoft}`,
  borderRadius: 10,
  background: PALETTE.accentTint,
};

const inviteGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))',
  gap: 12,
  alignItems: 'end',
};

const websiteLabelStyle = {
  color: PALETTE.ink3,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0,
  textTransform: 'uppercase',
};

const websiteHelpStyle = {
  maxWidth: 640,
  color: PALETTE.ink3,
  fontSize: 12,
  lineHeight: 1.45,
};
